import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseResponse } from '../models/base.response';
import { PegasusFlight, PegasusLoadPlan, PegasusOrder, PegasusPrediction, PegasusProduct } from '../models/pegasus.model';

export const endpoints = {
  getFlights: '/pegasus/flight',
  getFlightDetails: (id: string) => '/pegasus/flight/detail/' + id,
  triggerFlights: '/pegasus/flight/trigger-integration',
  exportFlights: '/pegasus/flight/export-to-excel',
  getProducts: '/pegasus/product',
  getProductDetails: (id: string) => '/pegasus/product/detail/' + id,
  triggerProducts: '/pegasus/product/trigger-integration',
  exportProducts: '/pegasus/product/export-to-excel',
  getLoadPlans: '/pegasus/load-plan',
  getLoadPlanDetails: (id: string) => '/pegasus/load-plan/detail/' + id,
  triggerLoadPlans: '/pegasus/load-plan/trigger-integration',
  exportLoadPlans: '/pegasus/load-plan/export-to-excel',
  getOrders: '/pegasus/order',
  getOrderDetails: (id: string) => '/pegasus/order/detail/' + id,
  exportOrders: '/pegasus/order/export-to-excel',
  triggerOrders: '/pegasus/order/trigger-integration',
  triggerPreorders: '/pegasus/order/trigger-preorder-integration',
  getPredictions: '/report/prediction-report',
  exportPredictions: '/report/prediction-report/export-to-excel',
  getCountingReport: '/report/counting-report',
  getDocoStaffReport: '/report/doco-staff-report',
  exportReport: (type: string) => '/report/' + type,
  getDocoStaffLoadList: '/report/doco-staff-load-list',
  getDocoStaffReturnList: '/report/doco-staff-return-list',
  triggerExecuteFlightGroupJob: '/pegasus/backoffice/trigger-executeFlightGroupJob',
  triggerCountingIRJob: '/pegasus/backoffice/trigger-countingIRJob'
};


@Injectable()
export class PegasusService {

  constructor(
    private http: HttpClient
  ) { }

  getFlights(query: NzTableQueryParams): Observable<BaseResponse<PegasusFlight[]>> {
    const url = environment.apiBaseUrl + endpoints.getFlights;
    return this.http.post<BaseResponse<PegasusFlight[]>>(url, query);
  }

  getFlightDetails(id: string): Observable<BaseResponse<PegasusFlight>> {
    const url = environment.apiBaseUrl + endpoints.getFlightDetails(id);
    return this.http.get<BaseResponse<PegasusFlight>>(url);
  }

  triggerFlights(): Observable<BaseResponse<boolean>> {
    const url = environment.apiBaseUrl + endpoints.triggerFlights;
    return this.http.post<BaseResponse<boolean>>(url, {});
  }

  exportFlights(query: NzTableQueryParams): Observable<HttpResponse<Blob>> {
    const url = environment.apiBaseUrl + endpoints.exportFlights;
    return this.http.post<Blob>(url, query, {
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }

  getProducts(query: NzTableQueryParams): Observable<BaseResponse<PegasusProduct[]>> {
    const url = environment.apiBaseUrl + endpoints.getProducts;
    return this.http.post<BaseResponse<PegasusProduct[]>>(url, query);
  }

  getProductDetails(id: string): Observable<BaseResponse<PegasusProduct>> {
    const url = environment.apiBaseUrl + endpoints.getProductDetails(id);
    return this.http.get<BaseResponse<PegasusProduct>>(url);
  }

  triggerProducts(): Observable<BaseResponse<boolean>> {
    const url = environment.apiBaseUrl + endpoints.triggerProducts;
    return this.http.post<BaseResponse<boolean>>(url, {});
  }

  exportProducts(query: NzTableQueryParams): Observable<HttpResponse<Blob>> {
    const url = environment.apiBaseUrl + endpoints.exportProducts;
    return this.http.post<Blob>(url, query, {
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }

  getLoadPlans(query: NzTableQueryParams): Observable<BaseResponse<PegasusLoadPlan[]>> {
    const url = environment.apiBaseUrl + endpoints.getLoadPlans;
    return this.http.post<BaseResponse<PegasusLoadPlan[]>>(url, query);
  }

  getLoadPlanDetails(id: string): Observable<BaseResponse<PegasusLoadPlan>> {
    const url = environment.apiBaseUrl + endpoints.getLoadPlanDetails(id);
    return this.http.get<BaseResponse<PegasusLoadPlan>>(url);
  }

  triggerLoadPlans(): Observable<BaseResponse<boolean>> {
    const url = environment.apiBaseUrl + endpoints.triggerLoadPlans;
    return this.http.post<BaseResponse<boolean>>(url, {});
  }

  exportLoadPlans(query: NzTableQueryParams): Observable<HttpResponse<Blob>> {
    const url = environment.apiBaseUrl + endpoints.exportLoadPlans;
    return this.http.post<Blob>(url, query, {
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }

  getOrders(query: NzTableQueryParams): Observable<BaseResponse<PegasusOrder[]>> {
    const url = environment.apiBaseUrl + endpoints.getOrders;
    return this.http.post<BaseResponse<PegasusOrder[]>>(url, query);
  }

  getOrderDetails(id: string): Observable<BaseResponse<PegasusOrder>> {
    const url = environment.apiBaseUrl + endpoints.getOrderDetails(id);
    return this.http.get<BaseResponse<PegasusOrder>>(url);
  }

  triggerOrders(): Observable<BaseResponse<boolean>> {
    const url = environment.apiBaseUrl + endpoints.triggerOrders;
    return this.http.post<BaseResponse<boolean>>(url, {});
  }

  triggerPreorders(): Observable<BaseResponse<boolean>> {
    const url = environment.apiBaseUrl + endpoints.triggerPreorders;
    return this.http.post<BaseResponse<boolean>>(url, {});
  }

  exportOrders(query: NzTableQueryParams): Observable<HttpResponse<Blob>> {
    const url = environment.apiBaseUrl + endpoints.exportOrders;
    return this.http.post<Blob>(url, query, {
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }

  getPredictions(query: NzTableQueryParams): Observable<BaseResponse<PegasusPrediction[]>> {
    const url = environment.apiBaseUrl + endpoints.getPredictions;
    return this.http.post<BaseResponse<PegasusPrediction[]>>(url, query);
  }

  exportPredictions(query: NzTableQueryParams): Observable<HttpResponse<Blob>> {
    const url = environment.apiBaseUrl + endpoints.exportPredictions;
    return this.http.post<Blob>(url, query, {
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }

  triggerExecuteFlightGroupJob(): Observable<BaseResponse<boolean>> {
    const url = environment.apiBaseUrl + endpoints.triggerExecuteFlightGroupJob;
    return this.http.post<BaseResponse<boolean>>(url, {});
  }

  triggerCountingIRJob(): Observable<BaseResponse<{ statusCode: number, message: string }>> {
    const url = environment.apiBaseUrl + endpoints.triggerCountingIRJob;
    return this.http.post<BaseResponse<{ statusCode: number, message: string }>>(url, {});
  }

  exportReport(query: NzTableQueryParams, type: string): Observable<HttpResponse<any>> {
    const url = environment.apiBaseUrl + endpoints.exportReport(type);
    return this.http.post<any>(url, query, {
      observe: 'response',
      responseType: 'blob' as 'json'
    }).pipe(catchError(this.handleError));
  }

  getDocoStaffReport(body: any): Observable<BaseResponse<any[]>> {
    const url = environment.apiBaseUrl + endpoints.getDocoStaffReport;
    return this.http.post<BaseResponse<any[]>>(url, body).pipe(catchError(this.handleError));
  }

  getDocoStaffLoadList(body: any): Observable<BaseResponse<any[]>> {
    const url = environment.apiBaseUrl + endpoints.getDocoStaffLoadList;
    return this.http.post<BaseResponse<any[]>>(url, body).pipe(catchError(this.handleError));
  }

  getDocoStaffReturnList(body: any): Observable<BaseResponse<any[]>> {
    const url = environment.apiBaseUrl + endpoints.getDocoStaffReturnList;
    return this.http.post<BaseResponse<any[]>>(url, body).pipe(catchError(this.handleError));
  }

  backwardStatus(flightGroupId: string): Observable<BaseResponse<boolean>> {
    const url = `${environment.apiBaseUrl}/flight/backwardStatus/${flightGroupId}`;
    return this.http.post<BaseResponse<boolean>>(url, {}).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

}
